import { FetchNextPageOptions } from "@tanstack/react-query";
import type { ChatAttachment, Enduser, GroupMMSMessage, InboxThread, User } from "@tellescope/types-models";
import type { TellescopeModelFromName } from "../../mappings";
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";
import { useUsersMap } from "./use-users-map";
import { useTellescopeQueries } from "../use-tellescope-queries";
import { useTellescopeInfiniteQuery } from "../use-tellescope-infinite-query";
import { TellescopeMutationInlineOptions } from "../types";
import type { ModelName, TellescopeQuery } from "../../types";
import { useTellescopeMutation } from "../use-tellescope-mutation";
import { useTellescopeQuery } from "../use-tellescope-query";
import { useCachedTellescopeQueries } from "../use-cached-tellescope-queries";
import { useMarkThreadReadOrUnread } from "./use-mark-thread-read-or-unread";
import { useEmailThreadQuery } from "./use-email-thread-query";
import { OPTIMISTIC_TICKET_THREAD_COMMENT_ID_PREFIX } from "../../mappings/mutation-updates/model-automatic-mutation-side-effect-configs/ticket-threads";
import { useTellescopeSession } from "../../context";

interface TransformationFunctionArgs<T> {
  list: T extends ModelName ? TellescopeModelFromName<T>[] : T extends Array<unknown> ? T : T[];
  endusersMap: Record<string, TellescopeModelFromName<"endusers">>;
  usersMap: Record<string, TellescopeModelFromName<"users">>;
  currentUserId: string;
}

type TransformationFunction<T> = ({ list, endusersMap, usersMap }: TransformationFunctionArgs<T>) => ThreadMessage[];

/**
 * Extracts file IDs from message content that contains {{files.<ID>.link:<NAME>}} patterns
 */
const extractFileIdsFromMessage = (message: string): string[] => {
  const filePattern = /\{\{files\.([a-zA-Z0-9]+)\.link:[^}]+\}\}/g;
  const matches = Array.from(message.matchAll(filePattern));
  return matches.flatMap((match) => {
    const id = match[1];
    return id !== undefined ? [id] : [];
  });
};

export const transformSMSToThreadMessages: TransformationFunction<"sms_messages"> = ({
  list,
  endusersMap,
  usersMap,
}): ThreadMessage[] => {
  return list.map((message) => ({
    id: message.id,
    isOutbound: !message.inbound,
    timestamp: message.timestamp ?? message.createdAt,
    content: message.templatedMessage || message.message,
    sender: message.inbound ? endusersMap[message.enduserId] : message.userId ? usersMap[message.userId] : undefined,
    error: message.error,
    isSending: !message.error && !message.delivered && !message.sendAt,
    isScheduled: !!message.sendAt && !message.delivered,
    isDraft: !!message.isDraft,
    sendAt: message.sendAt && !message.delivered ? message.sendAt || undefined : undefined,
    attachments: [
      ...(message.images ?? []).map(({ url, type }): ChatAttachment => ({ type, secureName: url })),
      ...(message.mediaURLs ?? []).map((url): ChatAttachment => ({ type: "", secureName: url })),
    ],
    fileIds: !message.inbound ? extractFileIdsFromMessage(message.message) : undefined,
  }));
};

export const transformGroupMMSToThreadMessages = ({
  list,
  endusersMap,
  usersMap,
}: Pick<TransformationFunctionArgs<"group_mms_conversations">, "usersMap" | "endusersMap"> & {
  list: TellescopeModelFromName<"group_mms_conversations">["messages"];
}): ThreadMessage[] => {
  return list.map<ThreadMessage>((message, i) => {
    const user = usersMap[message.sender];
    const enduser = endusersMap[message.sender];

    const isOutbound = !!user && !enduser;

    return {
      id: `group-mms-${i}`,
      isOutbound,
      timestamp: new Date(message.timestamp),
      content: message.message,
      sender: isOutbound ? user : enduser,
      isSending: false,
      isScheduled: false,
      isDraft: false,
    };
  });
};

export const transformChatToThreadMessages: TransformationFunction<"chats"> = ({ list, endusersMap, usersMap }) => {
  return list.reduce<ThreadMessage[]>((acc, message) => {
    if (message.senderId === null) return acc;
    const user = usersMap[message.senderId];
    const enduser = endusersMap[message.senderId];

    const isOutbound = !!user && !enduser;

    acc.push({
      id: message.id,
      isOutbound,
      timestamp: message.timestamp ?? message.createdAt,
      content: message.message,
      sender: isOutbound ? user : enduser,
      isSending: false,
      isScheduled: !!message.sendAt && !message.timestamp,
      isDraft: !!message.isDraft,
      sendAt: message.sendAt || undefined,
      attachments: message.attachments,
      mentions: message.mentions,
    });
    return acc;
  }, []);
};

export const transformEmailsToThreadMessages: TransformationFunction<"emails"> = ({ list, endusersMap, usersMap }) => {
  return list.reduce<ThreadMessage[]>((acc, email) => {
    const user = usersMap[email.userId];
    const enduser = email.enduserId ? endusersMap[email.enduserId] : undefined;

    const content = email.HTMLContent ?? email.textContent;
    const fileIds = !email.inbound ? extractFileIdsFromMessage(content) : undefined;

    acc.push({
      id: email.id,
      isOutbound: !email.inbound,
      timestamp: email.createdAt,
      content,
      sender: email.inbound ? enduser : user,
      emailMessageId: email.messageId,
      attachments: email.attachments,
      cc: email.cc,
      error: email.error,
      isSending: !email.error && !email.delivered && !email.sendAt,
      isScheduled: !!email.sendAt && !email.delivered,
      isDraft: !!email.isDraft,
      sendAt: email.sendAt && !email.delivered ? email.sendAt || undefined : undefined,
      fileIds,
    });

    return acc;
  }, []);
};

export const transformTicketThreadCommentsToThreadMessages: TransformationFunction<"ticket_thread_comments"> = ({
  list,
  endusersMap,
  usersMap,
  currentUserId,
}) => {
  return list.reduce<ThreadMessage[]>((acc, comment) => {
    const enduser = endusersMap[comment.enduserId];
    const isSending = comment.id.startsWith(OPTIMISTIC_TICKET_THREAD_COMMENT_ID_PREFIX);
    const user = isSending ? usersMap[currentUserId] : usersMap[comment.userId ?? ""] ?? usersMap[comment.creator];

    acc.push({
      id: comment.id,
      isOutbound: !comment.inbound,
      timestamp: comment.timestamp || comment.createdAt,
      content: comment.html || comment.plaintext,
      sender: comment.inbound ? enduser : user,
      attachments: comment.attachments,
      isSending,
      isScheduled: false,
      isDraft: false,
    });
    return acc;
  }, []);
};

export interface ThreadMessage {
  id: string;
  isOutbound: boolean;
  timestamp: Date;
  content: string;
  sender: Enduser | User | undefined;
  emailMessageId?: string;
  attachments?: ChatAttachment[];
  cc?: string[];
  error?: string;
  isSending: boolean;
  isDraft: boolean;
  isScheduled: boolean;
  sendAt?: Date;
  fileIds?: string[];
  mentions?: string[];
}

export interface UseInboxThreadResult {
  messages: ThreadMessage[];
  drafts: ThreadMessage[];
  scheduled: ThreadMessage[];
  loadNextPage: (options?: FetchNextPageOptions) => Promise<void>;
  metadata: InboxThread | undefined;
  isLoading: boolean;
  endusers: TellescopeModelFromName<"endusers">[];
  replyTo?: string;
  markReadOrUnread: (
    { userId, readStatus }: { userId: string; readStatus: "read" | "unread" },
    options?: TellescopeMutationInlineOptions<"inbox_threads", "updateOne">
  ) => Promise<TellescopeModelFromName<"inbox_threads"> | void>;
  toggleTag: (
    tagName: string,
    options?: TellescopeMutationInlineOptions<"inbox_threads", "updateOne">
  ) => Promise<TellescopeModelFromName<"inbox_threads">>;
  hasNextPage: boolean;
  toggleArchived: (
    options?: TellescopeMutationInlineOptions<"inbox_threads", "updateOne">
  ) => Promise<TellescopeModelFromName<"inbox_threads">>;
  toggleTrashed: (
    options?: TellescopeMutationInlineOptions<"inbox_threads", "updateOne">
  ) => Promise<TellescopeModelFromName<"inbox_threads">>;
  setAssignees: (
    newAssignees: string[],
    options?: TellescopeMutationInlineOptions<"inbox_threads", "updateOne">
  ) => Promise<TellescopeModelFromName<"inbox_threads"> | TellescopeModelFromName<"chat_rooms">>;
  filterByEnduserId: boolean;
  setFilterByEnduserId: Dispatch<SetStateAction<boolean>>;
}

export interface UseInboxConversationOptions {
  enabled?: boolean;
  initFilterByEnduserId?: boolean;
}

export const useInboxConversation = (
  threadId: string,
  { enabled = true, initFilterByEnduserId = true }: UseInboxConversationOptions = {}
): UseInboxThreadResult => {
  const [filterByEnduserId, setFilterByEnduserId] = useState(initFilterByEnduserId);
  const cachedMetadataSelect = useCallback(
    (queries: TellescopeQuery<"inbox_threads", "load_threads">[]) => {
      return queries.reduce<InboxThread | undefined>((acc, curr) => {
        if (acc) return acc;

        const match = curr.state.data?.pages.reduce<InboxThread | undefined>((acc, curr) => {
          if (acc) return acc;

          const match = curr.threads.find((thread) => thread.id === threadId);
          return match;
        }, undefined);
        return match;
      }, undefined);
    },
    [threadId]
  );
  const session = useTellescopeSession();

  const { data: cachedMetadata } = useCachedTellescopeQueries({
    model: "inbox_threads",
    method: "load_threads",
    select: cachedMetadataSelect,
  });

  const fetchedMetadata = useTellescopeInfiniteQuery({
    model: "inbox_threads",
    method: "load_threads",
    request: {
      autobuild: true,
      mdbFilter: {
        id: threadId,
      },
    },
    enabled: enabled && !cachedMetadata,
    getNextPageParam: (lastPage) => lastPage.threads.at(-1)?.timestamp,
    initialPageParam: undefined,
    paginateRequest: (lastTimestamp, request) => ({
      ...request,
      lastTimestamp,
    }),
    select: (data) => data.pages.at(0)?.threads.at(0),
  });

  const metadata = cachedMetadata ?? fetchedMetadata.data;

  const usersMap = useUsersMap();
  const endusersMap = useTellescopeQueries({
    model: "endusers",
    method: "getOne",
    requests: metadata?.enduserIds ?? [],
    combine: (queries) =>
      queries.reduce<Record<string, TellescopeModelFromName<"endusers">>>((acc, curr) => {
        if (curr.data) {
          acc[curr.data.id] = curr.data;
        }
        return acc;
      }, {}),
    enabled,
  });

  const smsMessagesQuery = useTellescopeInfiniteQuery({
    model: "sms_messages",
    method: "getSome",
    request: {
      mdbFilter: {
        phoneNumber: { $eq: metadata?.phoneNumber ?? "" },
        enduserPhoneNumber: { $eq: metadata?.enduserPhoneNumber ?? "" },
        enduserId: filterByEnduserId ? { $in: metadata?.enduserIds } : undefined,
      },
      limit: 25,
    },
    select: (data) =>
      data.pages.reduce<{
        messages: TellescopeModelFromName<"sms_messages">[];
        drafts: TellescopeModelFromName<"sms_messages">[];
        scheduled: TellescopeModelFromName<"sms_messages">[];
      }>(
        (acc, page) => {
          page.forEach((message) => {
            if (message.isDraft) {
              acc.drafts.push(message);
            } else if (message.sendAt && !message.delivered) {
              acc.scheduled.push(message);
            } else {
              acc.messages.push(message);
            }
          });

          return acc;
        },
        { messages: [], drafts: [], scheduled: [] }
      ),
    enabled:
      enabled && !!metadata && metadata?.type === "SMS" && !!metadata.phoneNumber && !!metadata.enduserPhoneNumber,
  });

  const groupMMSConversationQuery = useTellescopeQuery({
    model: "group_mms_conversations",
    method: "getOne",
    request: metadata?.threadId ?? "",
    enabled: enabled && !!metadata?.threadId && metadata?.type === "GroupMMS",
    select: (data) =>
      data.messages.reduce<{
        messages: GroupMMSMessage[];
      }>(
        (acc, curr) => {
          acc.messages.push(curr);
          return acc;
        },
        { messages: [] }
      ),
  });

  const chatMessagesQuery = useTellescopeInfiniteQuery({
    model: "chats",
    method: "getSome",
    request: {
      filter: {
        roomId: metadata?.threadId,
      },
    },
    enabled: enabled && !!metadata?.threadId && metadata?.type === "Chat",
    select: (data) =>
      data.pages.flat().reduce<{
        messages: TellescopeModelFromName<"chats">[];
        drafts: TellescopeModelFromName<"chats">[];
        scheduled: TellescopeModelFromName<"chats">[];
      }>(
        (acc, curr) => {
          if (curr.isDraft) {
            acc.drafts.push(curr);
          } else if (curr.sendAt) {
            acc.scheduled.push(curr);
          } else {
            acc.messages.push(curr);
          }
          return acc;
        },
        { messages: [], drafts: [], scheduled: [] }
      ),
  });

  const zendeskThreadCommentsQuery = useTellescopeInfiniteQuery({
    model: "ticket_thread_comments",
    method: "getSome",
    request: {
      filter: {
        ticketThreadId: metadata?.threadId,
      },
      sort: "oldFirst",
    },
    enabled: enabled && !!metadata?.threadId && metadata.type === "Zendesk",
    select: (data) => data.pages.flat(),
  });

  const endusers = useMemo(() => {
    return (
      metadata?.enduserIds.reduce<TellescopeModelFromName<"endusers">[]>((acc, id) => {
        const enduser = endusersMap[id];
        if (enduser) {
          acc.push(enduser);
        }
        return acc;
      }, []) ?? []
    );
  }, [endusersMap, metadata?.enduserIds]);

  const enduserId = endusers?.at(0)?.id;

  const emailsQuery = useEmailThreadQuery(
    {
      enduserId: enduserId ?? "",
      subjectLine: metadata?.title ?? "",
    },
    {
      enabled: enabled && !!enduserId && !!metadata?.title && metadata?.type === "Email",
      select: (data) =>
        data.reduce<{
          messages: TellescopeModelFromName<"emails">[];
          drafts: TellescopeModelFromName<"emails">[];
          scheduled: TellescopeModelFromName<"emails">[];
        }>(
          (acc, curr) => {
            if (curr.isDraft) {
              acc.drafts.push(curr);
            } else if (curr.sendAt && !curr.delivered) {
              acc.scheduled.push(curr);
            } else {
              acc.messages.push(curr);
            }
            return acc;
          },
          { messages: [], drafts: [], scheduled: [] }
        ),
    }
  );

  const query = useMemo(() => {
    switch (metadata?.type) {
      case "SMS":
        return smsMessagesQuery;
      case "GroupMMS":
        return groupMMSConversationQuery;
      case "Chat":
        return chatMessagesQuery;
      case "Email":
        return emailsQuery;
    }
  }, [chatMessagesQuery, emailsQuery, groupMMSConversationQuery, metadata?.type, smsMessagesQuery]);

  // Collect all file IDs from SMS messages
  const smsFileIds = useMemo(() => {
    if (metadata?.type !== "SMS") return [];

    const allMessages = [
      ...(smsMessagesQuery.data?.messages ?? []),
      ...(smsMessagesQuery.data?.drafts ?? []),
      ...(smsMessagesQuery.data?.scheduled ?? []),
    ];

    const fileIds = new Set<string>();
    allMessages.forEach((message) => {
      if (!message.inbound) {
        const ids = extractFileIdsFromMessage(message.message);
        ids.forEach((id) => fileIds.add(id));
      }
    });

    return Array.from(fileIds);
  }, [metadata?.type, smsMessagesQuery.data]);

  // Collect all file IDs from email messages
  const emailFileIds = useMemo(() => {
    if (metadata?.type !== "Email") return [];

    const allEmails = [
      ...(emailsQuery.data?.messages ?? []),
      ...(emailsQuery.data?.drafts ?? []),
      ...(emailsQuery.data?.scheduled ?? []),
    ];

    const fileIds = new Set<string>();
    allEmails.forEach((email) => {
      if (!email.inbound) {
        const content = email.HTMLContent ?? email.textContent;
        const ids = extractFileIdsFromMessage(content);
        ids.forEach((id) => fileIds.add(id));
      }
    });

    return Array.from(fileIds);
  }, [metadata?.type, emailsQuery.data]);

  // Combine all file IDs from both SMS and email messages
  const allFileIds = useMemo(() => {
    return [...smsFileIds, ...emailFileIds];
  }, [smsFileIds, emailFileIds]);

  // Fetch all files referenced in SMS and email messages
  const filesMap = useTellescopeQueries({
    model: "files",
    method: "getOne",
    requests: allFileIds,
    combine: (queries) =>
      queries.reduce<Record<string, TellescopeModelFromName<"files">>>((acc, curr) => {
        if (curr.data) {
          acc[curr.data.id] = curr.data;
        }
        return acc;
      }, {}),
    enabled: enabled && allFileIds.length > 0,
  });

  const { replyTo, messages, drafts, scheduled } = useMemo<{
    replyTo?: string;
    messages: ThreadMessage[];
    drafts: ThreadMessage[];
    scheduled: ThreadMessage[];
  }>(() => {
    const sortMessages = (msgs: ThreadMessage[]) => {
      return msgs.sort((a, b) => {
        // TODO: figure out why ISO strings aren't getting parsed here
        const aTimestamp = typeof a.timestamp === "string" ? new Date(a.timestamp) : a.timestamp;
        const bTimestamp = typeof b.timestamp === "string" ? new Date(b.timestamp) : b.timestamp;
        return aTimestamp.getTime() - bTimestamp.getTime();
      });
    };

    const sortScheduled = (msgs: ThreadMessage[]) =>
      msgs.sort((a, b) => {
        const aTime = a.sendAt?.getTime() ?? a.timestamp.getTime();
        const bTime = b.sendAt?.getTime() ?? b.timestamp.getTime();
        return aTime - bTime;
      });

    // Transform file data into ChatAttachment format for SMS messages
    const enrichThreadMessagesWithAttachments = (messages: ThreadMessage[]): ThreadMessage[] => {
      return messages.map((message) => {
        if (!message.fileIds || message.fileIds.length === 0) {
          return message;
        }

        const attachments: ChatAttachment[] = message.fileIds.reduce<ChatAttachment[]>((acc, fileId) => {
          const file = filesMap[fileId];
          if (!file) return acc;

          acc.push({
            secureName: file.secureName,
            type: file.type ?? "",
            name: file.name,
          } satisfies ChatAttachment);
          return acc;
        }, []);

        return {
          ...message,
          attachments: attachments.length > 0 ? attachments : message.attachments,
        };
      });
    };

    switch (metadata?.type) {
      case "SMS":
        return {
          messages: sortMessages(
            enrichThreadMessagesWithAttachments(
              transformSMSToThreadMessages({
                list: smsMessagesQuery.data?.messages ?? [],
                endusersMap,
                usersMap,
                currentUserId: session?.userInfo.id ?? "",
              })
            )
          ),
          drafts: sortMessages(
            enrichThreadMessagesWithAttachments(
              transformSMSToThreadMessages({
                list: smsMessagesQuery.data?.drafts ?? [],
                endusersMap,
                usersMap,
                currentUserId: session?.userInfo.id ?? "",
              })
            )
          ),
          scheduled: sortScheduled(
            enrichThreadMessagesWithAttachments(
              transformSMSToThreadMessages({
                list: smsMessagesQuery.data?.scheduled ?? [],
                endusersMap,
                usersMap,
                currentUserId: session?.userInfo.id ?? "",
              })
            )
          ),
        };
      case "GroupMMS":
        return {
          messages: sortMessages(
            transformGroupMMSToThreadMessages({
              list: groupMMSConversationQuery.data?.messages ?? [],
              endusersMap,
              usersMap,
            })
          ),
          drafts: [],
          scheduled: [],
        };
      case "Chat":
        return {
          messages: sortMessages(
            transformChatToThreadMessages({
              list: chatMessagesQuery.data?.messages ?? [],
              endusersMap,
              usersMap,
              currentUserId: session?.userInfo.id ?? "",
            })
          ),
          drafts: sortMessages(
            transformChatToThreadMessages({
              list: chatMessagesQuery.data?.drafts ?? [],
              endusersMap,
              usersMap,
              currentUserId: session?.userInfo.id ?? "",
            })
          ),
          scheduled: sortScheduled(
            transformChatToThreadMessages({
              list: chatMessagesQuery.data?.scheduled ?? [],
              endusersMap,
              usersMap,
              currentUserId: session?.userInfo.id ?? "",
            })
          ),
        };
      case "Email": {
        const replyTo = enduserId ? endusersMap[enduserId]?.email : undefined;
        return {
          replyTo,
          messages: sortMessages(
            enrichThreadMessagesWithAttachments(
              transformEmailsToThreadMessages({
                list: emailsQuery.data?.messages ?? [],
                endusersMap,
                usersMap,
                currentUserId: session?.userInfo.id ?? "",
              })
            )
          ),
          drafts: sortMessages(
            enrichThreadMessagesWithAttachments(
              transformEmailsToThreadMessages({
                list: emailsQuery.data?.drafts ?? [],
                endusersMap,
                usersMap,
                currentUserId: session?.userInfo.id ?? "",
              })
            )
          ),
          scheduled: sortScheduled(
            enrichThreadMessagesWithAttachments(
              transformEmailsToThreadMessages({
                list: emailsQuery.data?.scheduled ?? [],
                endusersMap,
                usersMap,
                currentUserId: session?.userInfo.id ?? "",
              })
            )
          ),
        };
      }
      case "Zendesk": {
        return {
          messages: enrichThreadMessagesWithAttachments(
            transformTicketThreadCommentsToThreadMessages({
              list: zendeskThreadCommentsQuery.data ?? [],
              endusersMap,
              usersMap,
              currentUserId: session?.userInfo.id ?? "",
            })
          ),
          drafts: [],
          scheduled: [],
        };
      }
    }
    return { messages: [], drafts: [], scheduled: [] };
  }, [
    chatMessagesQuery.data?.drafts,
    chatMessagesQuery.data?.messages,
    chatMessagesQuery.data?.scheduled,
    emailsQuery.data?.drafts,
    emailsQuery.data?.messages,
    emailsQuery.data?.scheduled,
    enduserId,
    endusersMap,
    filesMap,
    groupMMSConversationQuery.data?.messages,
    metadata?.type,
    session?.userInfo.id,
    smsMessagesQuery.data?.drafts,
    smsMessagesQuery.data?.messages,
    smsMessagesQuery.data?.scheduled,
    usersMap,
    zendeskThreadCommentsQuery.data,
  ]);

  const loadNextPage = useCallback(
    async (options?: FetchNextPageOptions) => {
      if (!query || !("fetchNextPage" in query)) return;

      query?.fetchNextPage(options);
    },
    [query]
  );

  const updateThreadMutation = useTellescopeMutation({
    model: "inbox_threads",
    method: "updateOne",
    autoCacheUpdateStrategy: "update",
  });

  const updateChatRoomMutation = useTellescopeMutation({
    model: "chat_rooms",
    method: "updateOne",
    autoCacheUpdateStrategy: "update",
  });

  const { markReadOrUnread } = useMarkThreadReadOrUnread({
    threadId,
    metadata,
    messages,
  });

  const toggleTag = useCallback<UseInboxThreadResult["toggleTag"]>(
    async (tagName, options) => {
      const filteredTags = metadata?.tags?.filter((name) => name !== tagName) ?? [];
      const shouldAdd = filteredTags.length === (metadata?.tags?.length ?? 0);

      return updateThreadMutation.mutateAsync(
        {
          id: threadId,
          tags: shouldAdd ? [...filteredTags, tagName] : filteredTags,
          _options: {
            replaceObjectFields: true,
          },
        },
        options
      );
    },
    [metadata?.tags, threadId, updateThreadMutation]
  );

  const toggleArchived = useCallback<UseInboxThreadResult["toggleArchived"]>(
    async (options) => {
      return updateThreadMutation.mutateAsync(
        {
          id: threadId,
          archivedAt: metadata?.archivedAt ? "" : new Date(),
          ...(metadata?.archivedAt ? {} : { trashedAt: "" }),
          _options: {
            replaceObjectFields: true,
          },
        },
        options
      );
    },
    [metadata?.archivedAt, threadId, updateThreadMutation]
  );

  const toggleTrashed = useCallback<UseInboxThreadResult["toggleTrashed"]>(
    async (options) => {
      return updateThreadMutation.mutateAsync(
        {
          id: threadId,
          trashedAt: metadata?.trashedAt ? "" : new Date(),
          ...(metadata?.trashedAt ? {} : { archivedAt: "" }),
          _options: {
            replaceObjectFields: true,
          },
        },
        options
      );
    },
    [metadata?.trashedAt, threadId, updateThreadMutation]
  );

  const setAssignees = useCallback<UseInboxThreadResult["setAssignees"]>(
    async (newAssignees, options) => {
      if (metadata?.type === "Chat" && metadata.threadId) {
        return updateChatRoomMutation.mutateAsync({
          id: metadata.threadId,
          userIds: newAssignees,
          assignedTo: newAssignees,
          _options: { replaceObjectFields: true },
        });
      }
      return updateThreadMutation.mutateAsync(
        {
          id: threadId,
          assignedTo: newAssignees,
          _options: { replaceObjectFields: true },
        },
        options
      );
    },
    [metadata?.type, metadata?.threadId, threadId, updateThreadMutation, updateChatRoomMutation]
  );

  return useMemo(
    () => ({
      metadata,
      loadNextPage,
      messages,
      drafts,
      scheduled,
      replyTo,
      endusers,
      isLoading: !!query?.isLoading || fetchedMetadata.isLoading,
      markReadOrUnread,
      toggleTag,
      hasNextPage: query && "hasNextPage" in query ? !!query?.hasNextPage : false,
      toggleArchived,
      toggleTrashed,
      setAssignees,
      filterByEnduserId,
      setFilterByEnduserId,
    }),
    [
      metadata,
      loadNextPage,
      messages,
      drafts,
      scheduled,
      replyTo,
      endusers,
      query,
      fetchedMetadata.isLoading,
      markReadOrUnread,
      toggleTag,
      toggleArchived,
      toggleTrashed,
      setAssignees,
      filterByEnduserId,
    ]
  );
};
